// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import type {Texture,Vector2} from 'three'
export interface InsertOpticsUniforms {uOpticsParams:{value:Texture};uOpticsAtlas:{value:Texture};uOpticsSelector:{value:Texture};uOpticsSelectorGroups:{value:number};uOpticsBoardSize:{value:Vector2};uOpticsTileCount:{value:number};uOpticsTileSize:{value:number}}
export const INSERT_OPTICS_DECLARATIONS = `
uniform sampler2D uOpticsParams,uOpticsAtlas,uOpticsSelector;
uniform vec2 uOpticsBoardSize;
uniform float uOpticsTileCount,uOpticsTileSize,uOpticsSelectorGroups;
varying float vInsertSurfaceZ;
struct HccLens { float physical,coverage,rim,index,transmission,peak; vec3 normal,colour,pigment; };
HccLens hccReadLens(vec2 uv,vec3 eyePH) {
 HccLens lens; lens.physical=0.0;lens.coverage=0.0;lens.rim=0.0;lens.index=0.0;lens.normal=vec3(0,0,1);lens.colour=vec3(0);lens.transmission=0.0;lens.peak=1.0;lens.pigment=vec3(0);
 vec2 boardDx=dFdx(uv)*uOpticsBoardSize,boardDy=dFdy(uv)*uOpticsBoardSize;
 // Nearest RGBA bytes contain exact candidates. No filtered identifier or ratio is used.
 for(int group=0;group<64;group++){
  if(float(group)>=uOpticsSelectorGroups)break;
  vec4 candidates=floor(texture2D(uOpticsSelector,vec2((float(group)+uv.x)/uOpticsSelectorGroups,uv.y))*255.0+.5);
  for(int channel=0;channel<4;channel++){
   float index=candidates[channel];if(index<1.0)break;
   float slot=(index-.5)/uLampCount;
   vec4 placement=texture2D(uOpticsParams,vec2(slot,.125));
   vec4 orientation=texture2D(uOpticsParams,vec2(slot,.375));
   float height=orientation.w>=1.0?orientation.w-1.0:-orientation.w;
   if(abs(vInsertSurfaceZ-height)>.002)continue;
   vec4 reference=texture2D(uOpticsParams,vec2(slot,.625));
   vec2 delta=(uv-.5)*uOpticsBoardSize-placement.xy;
   vec2 local=vec2(delta.x*orientation.x+delta.y*orientation.y,-delta.x*orientation.y+delta.y*orientation.x);
   vec2 tileUv=clamp(local/placement.zw/reference.w+.5,vec2(.5/uOpticsTileSize),vec2(1.0-.5/uOpticsTileSize));
   vec2 tileDx=vec2(boardDx.x*orientation.x+boardDx.y*orientation.y,-boardDx.x*orientation.y+boardDx.y*orientation.x)/placement.zw/reference.w;
   vec2 tileDy=vec2(boardDy.x*orientation.x+boardDy.y*orientation.y,-boardDy.x*orientation.y+boardDy.y*orientation.x)/placement.zw/reference.w;
   vec4 optical=textureGrad(uOpticsAtlas,vec2((orientation.z+tileUv.x)/uOpticsTileCount,tileUv.y),vec2(tileDx.x/uOpticsTileCount,tileDx.y),vec2(tileDy.x/uOpticsTileCount,tileDy.y));
   float distance=(optical.a*255.0-128.0)/127.0*reference.z;
   float aa=max((length(tileDx*reference.xy*reference.w)+length(tileDy*reference.xy*reference.w))*.5,1e-7);
   // Device-owned board pools keep their existing source map/rim; a conservative 3mm
   // domain covers the old three-texel stroke without accepting arbitrary remote IDs.
   if(orientation.w<.5){if(distance>-.003&&lens.physical<.5){lens.index=index;}continue;}
   float coverage=smoothstep(-aa,aa,distance);if(coverage<=0.0)continue;
   vec4 pigment=texture2D(uOpticsParams,vec2(slot,.875));
   // Refract toward the moulded underside: a bounded single-sample volume approximation.
   vec3 ray=refract(-normalize(eyePH),vec3(0,0,1),1.0/1.49);
   vec2 shift=vec2(ray.x*orientation.x+ray.y*orientation.y,-ray.x*orientation.y+ray.y*orientation.x)*(.0029/max(-ray.z,.1));
   vec2 underUv=clamp((local+shift)/placement.zw/reference.w+.5,vec2(.5/uOpticsTileSize),vec2(1.0-.5/uOpticsTileSize));
   vec4 underField=textureGrad(uOpticsAtlas,vec2((orientation.z+underUv.x)/uOpticsTileCount,underUv.y),vec2(tileDx.x/uOpticsTileCount,tileDx.y),vec2(tileDy.x/uOpticsTileCount,tileDy.y));
   float underDistance=(underField.a*255.0-128.0)/127.0*reference.z;
   float sourcePixel=max(reference.x,reference.y)*reference.w/uOpticsTileSize;
   float fieldCoverage=smoothstep(-sourcePixel,sourcePixel,underDistance);
   // Fill the clipped underside edge with scattered polymer light before filtering its contour.
   // The top outline is unchanged; no light or normal escapes its surface coverage.
   float continuousTransmission=clamp(underField.b+(1.0-fieldCoverage)*.62,0.0,1.0);
   float scaleToWorld=min(placement.z/reference.x,placement.w/reference.y);
   float scatterWidth=max(aa,.00045/max(scaleToWorld,.001));
   float softInterior=smoothstep(-scatterWidth,scatterWidth,underDistance);
   float underside=.22+.78*mix(.62,continuousTransmission,softInterior);
   float inset=min(reference.x,reference.y)*.012;
   float body=smoothstep(0.0,inset,distance);
   // A recessed seam and weak internal diffuser, confined to the actual lens surface.
   lens.physical=1.0;lens.coverage=coverage;lens.index=index;
   lens.rim=1.0-body;
   lens.colour=(vec3(.012)+pigment.rgb*.16)*mix(.18,1.0,body)*mix(.65,1.0,underside);lens.peak=pigment.a;lens.pigment=pigment.rgb;
   lens.transmission=underside*mix(.16,1.0,body);
   vec2 xy=optical.rg*2.0-1.0;vec3 n=vec3(xy,sqrt(max(1e-5,1.0-dot(xy,xy))));n.xy*=reference.xy/placement.zw;
   lens.normal=normalize(vec3(n.x*orientation.x-n.y*orientation.y,n.x*orientation.y+n.y*orientation.x,n.z));
  }
 }
 return lens;
}
`
export const INSERT_OPTICS_ROUGHNESS = `#include <roughnessmap_fragment>`
export const INSERT_OPTICS_NORMAL = `
#ifdef USE_NORMALMAP_TANGENTSPACE
 vec3 viewToEye=normalize(vViewPosition);
 vec3 eyePH=vec3(dot(tbn[0],viewToEye),dot(tbn[1],viewToEye),dot(tbn[2],viewToEye));
 HccLens hccLens=hccReadLens(vNormalMapUv,eyePH);
 diffuseColor.rgb=mix(diffuseColor.rgb,hccLens.colour,hccLens.coverage);
 roughnessFactor=mix(roughnessFactor,.26,hccLens.coverage);
 vec3 mapN=normalize(mix(vec3(0,0,1),hccLens.normal,hccLens.coverage));
 mapN.xy*=normalScale;normal=normalize(tbn*mapN);
#endif`
export const INSERT_OPTICS_LIGHT = `
if(hccLens.index>0.0){
 vec2 slot=vec2((hccLens.index-.5)/uLampCount,.5);vec3 state=texture2D(uLampState,slot).rgb;
 if(hccLens.physical>.5){float brightness=max(state.r,max(state.g,state.b))/max(hccLens.peak,.0001);totalEmissiveRadiance+=hccLens.pigment*brightness*hccLens.coverage*uLampGain*.72*hccLens.transmission;}
 else{vec4 cell=texture2D(uLampMap,vMapUv);float paintedIndex=floor(cell.r/max(cell.g,.0001)*255.0+.5);if(abs(paintedIndex-hccLens.index)<.5)totalEmissiveRadiance+=state*cell.g*uLampGain*(1.0+clamp(cell.b/max(cell.g,.0001),0.0,1.0)*.85);}
}`
